/**
 * cart-bulk-offers.js
 *
 * Handles tier-button clicks in the "Stock up & save" (.cbo__tier) zone.
 *
 * When a tier button is clicked, it:
 *  1. Reads data-line-key, data-qty, data-tier, data-props from the button.
 *  2. Merges existing properties with { _cart_bulk_tier: tier, _purchase_type: 'onetime' }.
 *  3. POSTs /cart/change.js using the line KEY (Shopify accepts key or 1-based index;
 *     key is more reliable here since we're not changing selling_plan).
 *  4. Refreshes the cart drawer via window.fetchCartDrawer() (same as cart-line-subscribe.js).
 *  5. Restores the cart-drawer scroll position using the same scheduleScrollRestore
 *     MutationObserver approach as cart-line-subscribe.js.
 *
 * Mutation lifecycle mirrors cart-line-subscribe.js exactly:
 *  - Respect window.cartIsMutating guard before starting
 *  - Set window.cartIsMutating = true and dispatch cart:mutation-start
 *  - Clear window.cartIsMutating = false and dispatch cart:mutation-end in finally
 *
 * Uses event delegation on document.body so handlers survive cart drawer re-renders.
 *
 * See: docs/superpowers/plans/2026-05-20-cart-upsell-phase4-bulk-card.md §B3
 */

(function () {
  'use strict';

  // Synchronous reentrancy guard. window.cartIsMutating is only set true AFTER an
  // initial `await fetch('/cart.js')`, so two fast clicks could both clear that check
  // before either set it. This flag is set synchronously before any await.
  var cboInFlight = false;

  /**
   * Restore the cart-drawer scroll position after the asynchronous,
   * fire-and-forget fetchCartDrawer() re-render.
   *
   * Mirrors CartLineSubscribe.scheduleScrollRestore in cart-line-subscribe.js:
   * Watches #CartDrawer .drawer__inner for the childList swap and restores
   * scrollTop on cart-drawer-items the moment the swap lands. The observer
   * is standalone so it outlives the re-render that replaces the drawer HTML.
   *
   * @param {number} savedScroll
   */
  function scheduleScrollRestore(savedScroll) {
    if (!savedScroll || savedScroll <= 0 || !('MutationObserver' in window)) return;
    var inner = document.querySelector('#CartDrawer .drawer__inner');
    if (!inner) return;

    function restore() {
      var s = document.querySelector('cart-drawer-items');
      if (s) s.scrollTop = savedScroll;
    }

    var obs = new MutationObserver(function () {
      restore();
      requestAnimationFrame(restore);
    });
    obs.observe(inner, { childList: true });
    // Safety net: stop observing after ~2.5s (same as cart-line-subscribe.js)
    setTimeout(function () { obs.disconnect(); }, 2500);
  }

  /**
   * Handle a .cbo__tier button click.
   * @param {HTMLElement} button
   */
  async function handleTierClick(button) {
    // Respect the global mutation guard — bail if another mutation is in flight.
    if (window.cartIsMutating || cboInFlight) return;

    var lineKey = button.dataset.lineKey;
    var qty = Number(button.dataset.qty);
    var tier = button.dataset.tier;
    var propsRaw = button.dataset.props;

    if (!lineKey || !qty || !tier) return;
    cboInFlight = true; // claim synchronously, before the first await

    // Source the line's CURRENT properties from /cart.js (authoritative) rather than
    // an HTML-embedded JSON blob — values like _eguide_copy can contain quotes/HTML that
    // would break a hand-built data-props string. /cart/change.js REPLACES properties when
    // a non-empty object is sent, so we must carry the existing ones forward.
    var existingProps = {};
    try {
      var cartJson = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      var line = (cartJson.items || []).find(function (it) { return it.key === lineKey; });
      if (line && line.properties) {
        Object.keys(line.properties).forEach(function (k) {
          // Skip empty-string props. They are leftover "cleared" markers (e.g. _bundle_type:''
          // written by the subscribe untoggle). Carrying them forward poisons the bulk-tier line:
          // the discount function reads _bundle_type:'' as Some("") and takes the bundle branch,
          // skipping the cart_bulk_tier branch, so no discount applies. Dropping them keeps the
          // bulk-tier line clean. (/cart/change.js REPLACES props, so omitting = removing them.)
          if (line.properties[k] != null && line.properties[k] !== '') existingProps[k] = line.properties[k];
        });
      }
    } catch (e) {
      // Fallback to the data-props snapshot if the cart fetch fails.
      if (propsRaw) {
        try { existingProps = JSON.parse(propsRaw); } catch (e2) { /* proceed with new props only */ }
      }
    }

    // Mode = subscribe only when the card's toggle is on AND this button carries a sub plan.
    var card = button.closest('.cbo__card');
    var subToggle = card ? card.querySelector('.cbo__sub-checkbox') : null;
    var subPlanId = (subToggle && subToggle.checked && button.dataset.subPlan) ? Number(button.dataset.subPlan) : null;

    // /cart/change.js REPLACES properties when a non-empty object is sent, so carry existing forward.
    var mergedProps;
    if (subPlanId) {
      // Subscribe bulk: the Recharge selling plan provides the discount on every order. Drop
      // _cart_bulk_tier so the one-time discount function never touches this subscription line.
      mergedProps = Object.assign({}, existingProps);
      delete mergedProps._cart_bulk_tier;
      mergedProps._purchase_type = 'subscription';
    } else {
      // One-time bulk: the discount function applies the tier % at checkout.
      mergedProps = Object.assign({}, existingProps, { _cart_bulk_tier: tier, _purchase_type: 'onetime' });
    }
    // Strip bulk-v2 / bundle markers so a product that is BOTH "Bulk Form" tagged AND
    // cart-bulk-eligible doesn't carry a stale _bundle_type. The discount fn checks
    // bundle_type BEFORE cart_bulk_tier, so a carried "1_bottle" would match 0% and suppress
    // the Buy 3/6 discount. (Mirrors cart-line-subscribe.js applyCartBulk.)
    delete mergedProps._bundle_type;
    delete mergedProps._bundle_quantity;
    delete mergedProps._frontend_discount;
    delete mergedProps._original_price;

    // Optimistic UI: dim the card while the mutation is in flight.
    if (card) {
      card.style.opacity = '0.55';
      card.style.pointerEvents = 'none';
    }

    // Capture scroll position BEFORE starting the mutation (same as cart-line-subscribe.js).
    var getScroller = function () { return document.querySelector('cart-drawer-items'); };
    var savedScroll = getScroller() ? getScroller().scrollTop : 0;

    // Mutation lifecycle start — mirrors CartLineSubscribe.performMutation.
    window.cartIsMutating = true;
    window.dispatchEvent(new CustomEvent('cart:mutation-start'));

    try {
      var resp = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(
          subPlanId
            ? { id: lineKey, quantity: qty, selling_plan: subPlanId, properties: mergedProps }
            : { id: lineKey, quantity: qty, properties: mergedProps }
        ),
      });

      if (!resp.ok) {
        var txt = await resp.text().catch(function () { return ''; });
        throw new Error('/cart/change.js failed (' + resp.status + '): ' + txt.slice(0, 200));
      }

      // Trigger cart drawer re-render — same pattern as cart-line-subscribe.js.
      // fetchCartDrawer() is fire-and-forget (starts fetch then renderCartContents).
      // Schedule scroll restore BEFORE calling fetchCartDrawer so the observer
      // is in place when .drawer__inner's childList mutates.
      if (typeof window.fetchCartDrawer === 'function') {
        scheduleScrollRestore(savedScroll);
        window.fetchCartDrawer();
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error('[cart-bulk-offers] Tier click failed', err);
      // Roll back optimistic dim.
      if (card) {
        card.style.opacity = '';
        card.style.pointerEvents = '';
      }
    } finally {
      // Mutation lifecycle end — always clear the guard and dispatch the event.
      window.cartIsMutating = false;
      cboInFlight = false;
      window.dispatchEvent(new CustomEvent('cart:mutation-end'));
    }
  }

  // ── Event delegation ────────────────────────────────────────────────────────
  // Attach to document.body so the handler survives cart drawer re-renders
  // (renderCartContents replaces #CartDrawer .drawer__inner innerHTML).
  document.body.addEventListener('click', function (e) {
    var button = e.target.closest('.cbo__tier');
    if (!button) return;
    e.preventDefault();
    handleTierClick(button);
  });

  // Subscribe toggle: swap each tier button's displayed final + save price between the
  // one-time and subscription values stored on the button's data attributes.
  document.body.addEventListener('change', function (e) {
    if (!e.target.matches('.cbo__sub-checkbox')) return;
    var card = e.target.closest('.cbo__card');
    if (!card) return;
    var sub = e.target.checked;
    card.querySelectorAll('.cbo__tier').forEach(function (btn) {
      var finalEl = btn.querySelector('.cbo__final');
      var saveEl = btn.querySelector('.cbo__save');
      var headEl = btn.querySelector('.cbo__tier-headline');
      var useSub = sub && btn.dataset.subFinal;
      if (finalEl) finalEl.textContent = useSub ? btn.dataset.subFinal : btn.dataset.onetimeFinal;
      if (saveEl) saveEl.textContent = 'SAVE ' + (useSub ? btn.dataset.subSave : btn.dataset.onetimeSave);
      // Mode word on the tier itself, so a tier can't be mistaken for a subscription
      // (or vice-versa) just because the toggle sits above it: "Buy 3 · one time"
      // <-> "3 bottles every 3 months".
      if (headEl) {
        var nextLabel = useSub ? btn.dataset.subLabel : btn.dataset.onetimeLabel;
        if (nextLabel) headEl.textContent = nextLabel;
      }
    });
  });

  // ── Clear the tier when the shopper manually edits a tiered line's quantity ──
  // The bulk discount is an explicit opt-in for a specific quantity (Buy 3 / Buy 6).
  // Once they hand-edit the qty it no longer matches the offer, so we drop
  // _cart_bulk_tier: the line reverts to regular price and the "Stock up & save"
  // card reappears for re-selection. The qty input for these lines is marked
  // data-cart-tier-line in cart-drawer.liquid, and Dawn's native handler is
  // patched to skip them, so this delegation owns their qty changes.
  async function clearTierOnQtyChange(qtyInput, newQty) {
    if (window.cartIsMutating || cboInFlight) return;
    var lineKey = qtyInput && qtyInput.dataset.lineKey;
    if (!lineKey || isNaN(newQty) || newQty < 0) return;
    cboInFlight = true; // claim synchronously, before the first await

    // Rebuild the line's properties WITHOUT _cart_bulk_tier (and without empty-string
    // markers). /cart/change.js REPLACES properties when a non-empty object is sent,
    // so omitting a key removes it. _purchase_type stays, so the object is non-empty.
    var props = {};
    try {
      var cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      var line = (cart.items || []).find(function (it) { return it.key === lineKey; });
      if (line && line.properties) {
        Object.keys(line.properties).forEach(function (k) {
          if (k === '_cart_bulk_tier') return;
          if (line.properties[k] != null && line.properties[k] !== '') props[k] = line.properties[k];
        });
      }
    } catch (e) { /* proceed with whatever props we have */ }

    var getScroller = function () { return document.querySelector('cart-drawer-items'); };
    var savedScroll = getScroller() ? getScroller().scrollTop : 0;

    window.cartIsMutating = true;
    window.dispatchEvent(new CustomEvent('cart:mutation-start'));
    try {
      var resp = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: newQty, properties: props }),
      });
      if (!resp.ok) {
        var txt = await resp.text().catch(function () { return ''; });
        throw new Error('/cart/change.js failed (' + resp.status + '): ' + txt.slice(0, 200));
      }
      if (typeof window.fetchCartDrawer === 'function') {
        scheduleScrollRestore(savedScroll);
        window.fetchCartDrawer();
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error('[cart-bulk-offers] Clearing tier on qty change failed', err);
    } finally {
      window.cartIsMutating = false;
      cboInFlight = false;
      window.dispatchEvent(new CustomEvent('cart:mutation-end'));
    }
  }

  document.body.addEventListener('click', function (e) {
    var btn = e.target.closest('quantity-input[data-cart-tier-line] .quantity__button');
    if (!btn) return;
    e.preventDefault();
    var qtyInput = btn.closest('quantity-input[data-cart-tier-line]');
    var input = qtyInput.querySelector('.quantity__input');
    var current = parseInt(input && input.value, 10) || 0;
    var newQty = btn.getAttribute('name') === 'minus' ? current - 1 : current + 1;
    if (newQty < 0) newQty = 0;
    clearTierOnQtyChange(qtyInput, newQty);
  });
  document.body.addEventListener('change', function (e) {
    if (!e.target.matches('quantity-input[data-cart-tier-line] .quantity__input')) return;
    var qtyInput = e.target.closest('quantity-input[data-cart-tier-line]');
    clearTierOnQtyChange(qtyInput, parseInt(e.target.value, 10));
  });
})();
